import { DRIFT_THRESHOLD, RoomRank } from "@anisync/types";
import type { MemberSyncView } from "@anisync/types";
import {
  CaptionsIcon,
  CheckIcon,
  CrownIcon,
  KeyboardIcon,
  Loader2Icon,
  MaximizeIcon,
  MinimizeIcon,
  PauseIcon,
  SendIcon,
  SkipForwardIcon,
  TriangleAlertIcon,
  UserXIcon,
  VolumeXIcon,
  XIcon,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { MediaWarningList } from "../components/media-warnings";
import { RoomInvite } from "../components/room-invite";
import { RoomNextEpisode } from "../components/room-next-episode";
import { RoomQueueAdd } from "../components/room-queue-add";
import { queuedEpisodesFor } from "../components/room-queue-rules";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { WrongVersionButton } from "../components/wrong-version-button";
import { usePlaybackSync } from "../hooks/use-playback-sync";
import { useRoom } from "../hooks/use-room";
import { type PlayStatus, getPlayStatus } from "../lib/api";
import { readFlag, writeFlag } from "../lib/device-pref";
import { formatClock } from "../lib/format";
import { cn } from "../lib/utils";
import { BandwidthHint } from "../player/bandwidth-hint";
import { DownloadState } from "../player/download-state";
import { PlayerOsd, useChromeVisible } from "../player/osd";
import { ShortcutHelp, useShortcutHelp } from "../player/shortcut-help";
import { useImmersive } from "../player/use-immersive";
import { usePlayerShortcuts } from "../player/use-shortcuts";
import { skipLabel, useSkipRanges } from "../player/use-skip-ranges";
import { useSubtitles } from "../player/use-subtitles";
import { useVideoFit, videoFitStyle } from "../player/use-video-fit";
import { OnScreenKeyboard } from "../tv/on-screen-keyboard";

/**
 * Pokój wspólnego oglądania.
 *
 * Odtwarzacz jest tu prostszy niż na ekranie samotnym: nie ma własnego
 * raportowania postępu ani wznawiania od zapisanej pozycji, bo pozycję narzuca
 * prowadzący. Wznawianie „od 12:34" w pokoju rozjechałoby wszystkich naraz.
 */
/**
 * Wolny takt sprawdzania stanu materiału.
 *
 * Szybkie zmiany przychodzą po WebSocket; to jest wyłącznie zabezpieczenie na
 * zgubione zdarzenie oraz sposób na dociągnięcie adresu strumienia.
 */
const SAFETY_CHECK_MS = 5000;

/** Jak często zgłaszamy własną pozycję. Próg korekty dryfu to dwie sekundy. */
const HEARTBEAT_MS = 2000;

/** Ten sam klucz co na ekranie wyszukiwania: preferencja należy do urządzenia. */
const KEYBOARD_PREF = "onScreenKeyboard";

export function RoomScreen() {
  const { slug = "" } = useParams();
  const room = useRoom(slug);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isLeader = room.myUserKey !== null && room.myUserKey === room.leaderUserKey;
  /**
   * Czy mogę zmieniać kolejkę.
   *
   * Ta sama reguła co na serwerze: gość z rolą `viewer` nie może. Liczymy ją
   * z listy uczestników, bo tylko serwer wie, z jakim zaproszeniem ktoś wszedł.
   */
  const me = room.members.find((m) => m.userKey === room.myUserKey);
  const mayEditQueue = me === undefined || !me.isGuest || me.role === "controller";
  const mediaFileId = room.current?.mediaFileId ?? null;

  /**
   * Stan materiału: szybkie zmiany z WebSocketa, sprawdzenie z HTTP.
   *
   * Postęp pobierania rozsyła serwer jednym strumieniem do całego pokoju, więc
   * dziesięć osób czekających na ten sam odcinek to jeden nadawca, a nie
   * dziesięć odpytywań co sekundę. Odpytywanie zostaje w wolnym takcie z dwóch
   * powodów: adres strumienia przychodzi tylko po HTTP, a zgubione zdarzenie nie
   * może zostawić widza na zawsze w stanie „pobieram".
   */
  const [status, setStatus] = useState<PlayStatus | null>(null);
  /** Komunikat po „Zła wersja” — znika z przełączeniem materiału. */
  const [rejectNote, setRejectNote] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: celowo zależne tylko od materiału
  useEffect(() => {
    setRejectNote(null);
  }, [mediaFileId]);
  useEffect(() => {
    if (mediaFileId === null) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const check = async (): Promise<void> => {
      try {
        const next = await getPlayStatus(mediaFileId);
        if (cancelled) return;
        setStatus(next);
        if (next.state !== "ready" && next.state !== "failed") {
          timer = setTimeout(() => void check(), SAFETY_CHECK_MS);
        }
      } catch {
        // Pojedyncza nieudana próba nie jest awarią — kolejny takt ją powtórzy.
        if (!cancelled) timer = setTimeout(() => void check(), SAFETY_CHECK_MS);
      }
    };
    void check();

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [mediaFileId]);

  /**
   * Postęp z pokoju nakładamy na znany stan.
   *
   * Dojście do stanu końcowego dociąga pełny stan po HTTP: zdarzenie postępu nie
   * niesie adresu strumienia, a bez niego nie ma czego odtwarzać.
   */
  const wsProgress = room.progress;
  useEffect(() => {
    if (wsProgress === null || mediaFileId === null) return;
    if (wsProgress.mediaFileId !== mediaFileId) return;

    if (wsProgress.state === "ready" || wsProgress.state === "failed") {
      getPlayStatus(mediaFileId)
        .then(setStatus)
        .catch(() => undefined);
      return;
    }
    setStatus((prev) => (prev === null ? prev : { ...prev, ...wsProgress }));
  }, [wsProgress, mediaFileId]);

  const ready = status?.state === "ready" && status.streamUrl !== null;

  const onBroadcast = useCallback(
    (time: number, paused: boolean) => room.send.update(time, paused),
    [room.send],
  );
  const onEnded = useCallback(() => room.send.ended(), [room.send]);

  /**
   * Napisy renderujemy KAŻDEMU niezależnie, i to jest właściwe: warstwa napisów
   * jest lokalna, nie wchodzi w synchronizację pozycji, a jeden widz może chcieć
   * je wyłączyć albo przesunąć bez wpływu na pozostałych.
   */
  const subtitles = useSubtitles({ videoRef, mediaFileId: mediaFileId ?? "", ready });

  /**
   * Zakresy pomijania też wczytuje każdy, ale PRZESKAKIWAĆ może tylko
   * prowadzący. Przeskok followera cofnąłby natychmiast jego własny takt korekty
   * dryfu — wyglądałoby to jak zepsuty odtwarzacz, a nie jak brak uprawnienia.
   */
  const skip = useSkipRanges({
    videoRef,
    titleId: room.current?.titleId ?? null,
    episodeNumber: room.current?.episodeNumber ?? null,
    /**
     * Bez czasu trwania: w pokoju nie mamy stanu materiału pod ręką, a czytanie
     * `videoRef.current.duration` w trakcie renderu dałoby `NaN` przed wczytaniem
     * metadanych — i, co gorsza, `NaN` w tablicy zależności odpytywałby serwer przy
     * każdym renderze, bo `NaN !== NaN`. Skutek braku liczby jest znośny: AniSkip
     * nie sprawdzi wtedy zgodności wydania.
     */
    durationSec: null,
    ready,
  });

  const immersive = useImmersive({ containerRef, videoRef, active: ready });
  /**
   * Kadr mieści się w oknie — ta sama poprawka co w odtwarzaczu solo.
   *
   * Tu mierzymy wobec KOLUMNY z obrazem, nie wobec całego ekranu: obok stoi rail
   * z czatem, który bywa wyższy od obrazu, a od jego wysokości kadr zależeć nie może.
   */
  const columnRef = useRef<HTMLDivElement>(null);
  const fit = useVideoFit({
    containerRef,
    rootRef: columnRef,
    enabled: !immersive.isFullscreen,
    ready,
  });
  const chromeVisible = useChromeVisible(containerRef, videoRef, ready);
  const { open: helpOpen, setOpen: setHelpOpen } = useShortcutHelp();

  /**
   * Takt zgłoszeniowy: mówimy serwerowi, gdzie jesteśmy w materiale.
   *
   * Wysyła KAŻDY uczestnik, nie tylko prowadzący — inaczej nie da się powiedzieć,
   * czy gość widzi to samo, a rozjechany widz sam o tym nie wie, bo u niego obraz
   * idzie normalnie. Dwie sekundy: odchylenie powyżej dwóch sekund i tak wywołuje
   * korektę, więc częstsze zgłoszenia nie dodałyby informacji.
   */
  useEffect(() => {
    if (!ready) return;
    const timer = setInterval(() => {
      const video = videoRef.current;
      if (video === null) return;
      room.send.heartbeat(video.currentTime, video.paused);
    }, HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [ready, room.send]);

  /**
   * Skróty w pokoju są ROZDZIELONE według tego, czy zmieniają coś wspólnego.
   *
   * Pełny ekran, głośność, wyciszenie i napisy są lokalne — działają dla
   * każdego. Pauza i przewijanie należą do prowadzącego: skrót followera
   * rozjechałby pokój, a jego własny takt korekty dryfu natychmiast cofnąłby
   * zmianę — wyglądałoby to na zepsuty odtwarzacz, nie na brak uprawnienia.
   */
  usePlayerShortcuts({
    videoRef,
    enabled: ready,
    canControlPlayback: isLeader,
    hasActiveSubtitles: subtitles.activeId !== null,
    actions: {
      onToggleFullscreen: immersive.toggleFullscreen,
      onToggleSubtitles: () =>
        subtitles.select(subtitles.activeId !== null ? null : (subtitles.tracks[0]?.id ?? null)),
      onSkip: isLeader && skip.current !== null ? skip.skip : undefined,
    },
  });

  const { correctedAt } = usePlaybackSync({
    videoRef,
    sync: room.sync,
    offsetMs: room.offsetMs,
    isLeader,
    ready,
    mediaEpoch: room.mediaEpoch,
    onBroadcast,
    onEnded,
  });

  /**
   * Wyrzucenie kończy pobyt w pokoju w tej karcie.
   *
   * Pokazujemy powód i NIE próbujemy wejść ponownie: automatyczne ponowienie
   * po zerwaniu połączenia dobijałoby serwer żądaniami, które i tak zostaną
   * odrzucone, i wyglądałoby jak zapętlona awaria.
   */
  if (room.kickedReason !== null) {
    return (
      <Card className="mx-auto max-w-md border-destructive/40 bg-destructive/5">
        <CardHeader>
          <CardTitle className="text-lg text-destructive">Nie jesteś już w tym pokoju</CardTitle>
          <CardDescription>{room.kickedReason}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5 lg:flex-row">
      {/* Ściągawka skrótów — w pokoju tym bardziej potrzebna, bo część klawiszy
          działa tylko u prowadzącego i bez opisu nie da się tego odgadnąć. */}
      <ShortcutHelp open={helpOpen} onClose={() => setHelpOpen(false)} />

      <div ref={columnRef} className="flex min-w-0 flex-1 flex-col gap-4">
        <header className="flex flex-wrap items-baseline justify-between gap-3">
          <div className="flex flex-col">
            <h1 className="text-xl font-semibold">{room.snapshot?.name ?? slug}</h1>
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              {/* Kropka stanu połączenia: w pokoju to informacja najważniejsza,
                  bo zerwane połączenie wygląda jak zatrzymany odtwarzacz. */}
              <span
                className={cn(
                  "size-1.5 rounded-full",
                  room.connected ? "bg-success" : "animate-pulse bg-warning",
                )}
              />
              {room.connected ? `${room.members.length} w pokoju` : "łączę…"}
              {room.offsetMs !== 0 &&
                ` · zegar ${room.offsetMs > 0 ? "+" : ""}${Math.round(room.offsetMs)} ms`}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {isLeader ? (
              <Badge variant="accent">
                <CrownIcon />
                prowadzisz
              </Badge>
            ) : (
              <Button variant="outline" size="sm" onClick={() => room.send.claimLeader()}>
                <CrownIcon />
                Przejmij prowadzenie
              </Button>
            )}

            {/*
              Zaproszenie w NAGŁÓWKU pokoju, nie w panelu bocznym.
              Wspólne oglądanie zaczyna się od wpuszczenia kogoś, więc to pierwsza
              rzecz, której się tu szuka — a panel na wąskim ekranie jest pod
              obrazem, czyli poza pierwszym spojrzeniem.
            */}
            <RoomInvite slug={slug} roomName={room.snapshot?.name ?? slug} />
          </div>
        </header>

        {room.error !== null && (
          <Alert variant="destructive">
            <AlertDescription>{room.error}</AlertDescription>
          </Alert>
        )}

        {room.current === null && (
          <Card>
            <CardContent className="py-4 text-muted-foreground">
              Kolejka jest pusta. Dodaj odcinek ze strony tytułu, żeby zacząć.
            </CardContent>
          </Card>
        )}

        {/*
          Pozycja bez materiału ma SWÓJ widok.
          Wcześniej nie renderowało się tu nic: brak pliku znaczył brak stanu do
          pokazania, więc pokój wyglądał na zepsuty dokładnie w tej chwili, w której
          serwer szukał źródeł odcinka.
        */}
        {room.current !== null && mediaFileId === null && (
          <Card>
            <CardContent className="flex items-center gap-3 py-4 text-muted-foreground">
              <Loader2Icon className="size-4 animate-spin" />
              <span>
                Szukam źródeł: {room.current.titleLabel}
                {room.current.episodeNumber !== null && ` — odcinek ${room.current.episodeNumber}`}
              </span>
            </CardContent>
          </Card>
        )}

        {room.current !== null && !ready && status !== null && <DownloadState status={status} />}

        {ready && status?.streamUrl != null && (
          // Wyśrodkowanie na rodzicu, nie przez `mx-auto` na kadrze: ten walczyłby
          // o `margin-inline` z ujemnym marginesem `bleed-edge` na wąskim ekranie.
          <div className="flex justify-center max-md:bleed-edge">
            {/* `relative`, bo libass wstawia własne płótno jako rodzeństwo wideo
                i pozycjonuje je absolutnie. Szerokość z pomiaru, wysokość
                z proporcji — patrz `useVideoFit`. */}
            <div
              ref={containerRef}
              style={videoFitStyle(fit)}
              className="relative aspect-video w-full overflow-hidden bg-black shadow-2xl shadow-black/40 md:rounded-xl md:border md:border-border"
            >
              {/*
              Sterowanie rysujemy sami także tutaj, a przewijanie zostaje
              WYŁĄCZNIE u prowadzącego: pasek followera cofnąłby się po sekundzie
              przy najbliższej korekcie dryfu, co wygląda na zepsuty odtwarzacz,
              a nie na brak uprawnienia.
            */}
              {/* biome-ignore lint/a11y/useMediaCaption: napisy ASS renderuje libass
                na osobnej warstwie — element `track` nie zna tego formatu.
                Materiał bywa też z napisami wtopionymi w obraz. */}
              <video
                ref={videoRef}
                src={status.streamUrl}
                autoPlay
                preload="auto"
                className="h-full w-full bg-black"
              />

              {/*
              Nakładka z pauzą TYLKO dla prowadzącego: u followera stuknięcie
              zatrzymałoby obraz, który po sekundzie wróciłby sam przy najbliższej
              korekcie dryfu — wygląda to na zepsuty odtwarzacz, nie na brak
              uprawnienia.
            */}
              {isLeader && (
                <button
                  type="button"
                  onClick={() => {
                    const video = videoRef.current;
                    if (video === null) return;
                    if (video.paused) void video.play().catch(() => undefined);
                    else video.pause();
                  }}
                  className="focus-ring-own absolute inset-0 z-0 cursor-default outline-none"
                  tabIndex={-1}
                  aria-label="Wstrzymaj albo odtwórz"
                />
              )}

              <PlayerOsd
                videoRef={videoRef}
                durationSec={status.durationSec}
                spriteVttUrl={
                  mediaFileId !== null ? `/api/v1/media/${mediaFileId}/sprite.vtt` : null
                }
                canControl={isLeader}
                visible={chromeVisible}
              />

              {/* Wymagane pasmo — w pokoju tym bardziej, bo zacinanie u jednej osoby
                wygląda dla resztki jak rozjechana synchronizacja. */}
              <BandwidthHint
                videoRef={videoRef}
                sizeBytes={status.sizeBytes}
                durationSec={status.durationSec}
              />

              {skip.current !== null && isLeader && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={skip.skip}
                  className="absolute right-4 bottom-20 z-10 shadow-lg"
                >
                  <SkipForwardIcon />
                  {skipLabel(skip.current.kind)}
                </Button>
              )}

              {/* Wewnątrz kontenera, więc przeżywa pełny ekran. */}
              <div className="absolute top-3 right-3 z-10 flex items-center gap-1 rounded-lg bg-background/70 p-1 backdrop-blur-sm">
                {subtitles.tracks.length > 0 && (
                  <Button
                    variant={subtitles.activeId !== null ? "secondary" : "ghost"}
                    size="icon"
                    onClick={() =>
                      subtitles.select(
                        subtitles.activeId !== null ? null : (subtitles.tracks[0]?.id ?? null),
                      )
                    }
                    aria-pressed={subtitles.activeId !== null}
                    aria-label={subtitles.activeId !== null ? "Wyłącz napisy" : "Włącz napisy"}
                    title="Napisy (C)"
                  >
                    <CaptionsIcon />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={immersive.toggleFullscreen}
                  aria-label={immersive.isFullscreen ? "Wyjdź z pełnego ekranu" : "Pełny ekran"}
                  title="Pełny ekran (F)"
                >
                  {immersive.isFullscreen ? <MinimizeIcon /> : <MaximizeIcon />}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/*
          Wybór ścieżki napisów. Bez przesunięcia i bez skrótów, inaczej niż
          w odtwarzaczu samotnym: w pokoju liczy się to, żeby wszyscy widzieli tę
          samą klatkę, a rozstrajanie napisów u siebie jest tu mało przydatne —
          i tak trafiają do materiału dobranego przez prowadzącego.
        */}
        {ready && subtitles.tracks.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <CaptionsIcon className="size-4" />
              Napisy
            </span>
            {subtitles.tracks.map((track) => (
              <Button
                key={track.id}
                variant={track.id === subtitles.activeId ? "secondary" : "ghost"}
                size="sm"
                onClick={() => subtitles.select(track.id)}
              >
                {track.label ?? track.lang.toUpperCase()}
              </Button>
            ))}
            <Button
              variant={subtitles.activeId === null ? "secondary" : "ghost"}
              size="sm"
              onClick={() => subtitles.select(null)}
            >
              Wyłączone
            </Button>
          </div>
        )}

        {/*
          Ostrzeżenia i „Zła wersja” także w pokoju — tu ogląda się najczęściej, więc
          tu najczęściej ktoś zauważy francuskie napisy. Plik zostaje (serwer nie
          urywa seansu wszystkim naraz), ale kopia jest zapamiętana, więc ponowne
          dodanie odcinka pobierze inną — i komunikat mówi to wprost.
        */}
        {ready && status !== null && <MediaWarningList warnings={status.warnings ?? []} />}
        {ready && status?.rejectable === true && mediaFileId !== null && (
          <div className="flex flex-wrap items-center gap-2">
            <WrongVersionButton
              mediaFileId={mediaFileId}
              onDone={(result) =>
                setRejectNote(
                  result.kept
                    ? "Zapamiętano jako złą wersję dla wszystkich. Pokój odtwarza dalej ten plik — usuń pozycję z kolejki i dodaj odcinek ponownie, a pobierze się inna kopia."
                    : "Zapamiętano jako złą wersję. Pobieram inną kopię — dodaj odcinek do kolejki ponownie.",
                )
              }
            />
          </div>
        )}
        {rejectNote !== null && (
          <Alert variant="info">
            <AlertDescription>{rejectNote}</AlertDescription>
          </Alert>
        )}

        {/* Sterowanie tylko u prowadzącego: gdyby follower mógł przewijać,
            jego własny takt korekty natychmiast cofnąłby zmianę i wyglądałoby to
            jak zepsuty odtwarzacz. */}
        {ready && !isLeader && (
          <p className="text-sm text-muted-foreground">
            Pozycję ustala prowadzący. Przejmij prowadzenie, żeby przewijać.
          </p>
        )}

        {correctedAt !== null && Date.now() - correctedAt < 4000 && (
          <p className="text-sm text-warning">Rozjechana synchronizacja — poprawiono</p>
        )}

        {room.advanceIn !== null && (
          <Alert variant="info">
            <AlertDescription>Następna pozycja za {room.advanceIn} s</AlertDescription>
          </Alert>
        )}

        {ready && <Reactions room={room} />}

        <Queue room={room} isLeader={isLeader} mayEdit={mayEditQueue} />
      </div>

      {/*
        Panel przyklejony i przewijany OSOBNO na szerokim ekranie: czat rosnący
        w dół wydłużał całą stronę, więc obraz odjeżdżał do góry i trzeba było
        przewijać z powrotem, żeby cokolwiek zobaczyć.
      */}
      <aside className="flex w-full shrink-0 flex-col gap-4 lg:sticky lg:top-16 lg:max-h-[calc(100svh-5rem)] lg:w-80 lg:overflow-y-auto lg:pr-1 xl:w-96">
        {/*
          Panel dodawania widzą wszyscy POZA gościem z zaproszeniem „tylko
          oglądać" — dokładnie ci, których wpuszcza serwer. Pokazywanie go
          reszcie znaczyłoby przycisk, który zawsze kończy się odmową.
        */}
        {/*
          Najczęstsza czynność w trakcie seansu stoi NAD panelem wyszukiwania:
          dołożenie kolejnego odcinka tej samej serii. Przez panel dało się to
          zrobić od dawna, ale trzema krokami — wyszukaj tytuł, wejdź w niego,
          znajdź numer — mimo że numer wynika wprost z tego, co właśnie leci.
        */}
        {mayEditQueue && room.current !== null && (
          <RoomNextEpisode
            titleId={room.current.titleId}
            titleLabel={room.current.titleLabel}
            episodeNumber={room.current.episodeNumber}
            queuedEpisodes={queuedEpisodesFor(room.queue, room.current.titleId)}
            onAdd={(input) => room.send.queueAdd(input)}
          />
        )}

        {mayEditQueue && <RoomQueueAdd onAdd={(input) => room.send.queueAdd(input)} />}

        <Members room={room} />
        <Chat room={room} />
      </aside>
    </div>
  );
}

/**
 * Reakcje. Ulotne i niezapisywane — to sygnał „widzę to samo co ty", a nie treść.
 * Dlatego nie trafiają do czatu ani do bazy.
 */
function Reactions({ room }: { room: ReturnType<typeof useRoom> }) {
  const palette = ["😂", "😮", "😭", "🔥", "❤️", "👏"];
  return (
    <div className="flex flex-wrap items-center gap-2">
      {palette.map((emoji) => (
        <Button
          key={emoji}
          variant="outline"
          size="sm"
          onClick={() => room.send.reaction(emoji)}
          className="text-base transition-transform duration-150 hover:scale-110"
        >
          {emoji}
        </Button>
      ))}
      <span className="ml-2 flex min-h-6 items-center gap-1 text-lg">
        {room.reactions.map((r) => (
          <span key={r.id} title={r.displayName}>
            {r.emoji}
          </span>
        ))}
      </span>
    </div>
  );
}

function Members({ room }: { room: ReturnType<typeof useRoom> }) {
  const me = room.members.find((m) => m.userKey === room.myUserKey);
  // Moderować można tylko osoby o NIŻSZEJ randze — ta sama reguła co na serwerze,
  // powtórzona tu wyłącznie po to, żeby nie pokazywać przycisków, które odmówią.
  const canModerate = (targetRank: number): boolean =>
    me !== undefined && me.rank >= RoomRank.Moderator && targetRank < me.rank;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          Uczestnicy
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col gap-1.5">
          {room.members.map((m) => (
            <li key={m.userKey} className="flex flex-wrap items-center gap-2 text-sm">
              <Avatar className="size-6">
                <AvatarFallback className="text-[0.625rem]">
                  {m.displayName.slice(0, 2).toLocaleUpperCase("pl")}
                </AvatarFallback>
              </Avatar>
              <span className={m.isLeader ? "font-medium text-accent" : undefined}>
                {m.displayName}
              </span>
              {m.isLeader && <CrownIcon className="size-3.5 text-accent" aria-label="prowadzi" />}
              {m.isGuest && <span className="text-xs text-muted-foreground">gość</span>}
              {m.muted && <Badge variant="warning">wyciszony</Badge>}
              {m.userKey === room.myUserKey && (
                <span className="text-xs text-muted-foreground">to Ty</span>
              )}

              {/*
                Zgodność z odtwarzaniem — powód, dla którego ta lista istnieje
                w ogóle: pokazywała KTO jest w pokoju, a nie CZY widzi to samo.
                Rozjechany widz nie wie o tym sam, bo u niego obraz idzie normalnie.
              */}
              <SyncBadge state={room.memberSync.get(m.userKey) ?? null} />

              {canModerate(m.rank) && (
                <span className="ml-auto flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    onClick={() => room.send.mute(m.userKey, !m.muted)}
                    aria-label={m.muted ? "Odcisz" : "Wycisz"}
                  >
                    <VolumeXIcon />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 hover:text-destructive"
                    onClick={() => room.send.kick(m.userKey)}
                    aria-label="Wyrzuć"
                  >
                    <UserXIcon />
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function Queue({
  room,
  isLeader,
  mayEdit,
}: {
  room: ReturnType<typeof useRoom>;
  isLeader: boolean;
  mayEdit: boolean;
}) {
  if (room.queue.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          Kolejka
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="flex flex-col gap-1">
          {room.queue.map((q) => {
            const isCurrent = q.uid === room.current?.uid;
            return (
              <li
                key={q.uid}
                className={cn(
                  "flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm",
                  isCurrent && "bg-accent/10",
                )}
              >
                <span className={isCurrent ? "font-medium text-accent" : undefined}>
                  {q.titleLabel}
                  {q.episodeNumber !== null && ` — odcinek ${q.episodeNumber}`}
                  {q.durationSec !== null && (
                    <span className="ml-2 text-xs text-muted-foreground tabular-nums">
                      {formatClock(q.durationSec)}
                    </span>
                  )}
                  {/* Pozycja może wskazywać materiał jeszcze nie pobrany — to
                      normalny stan kolejki, więc pokazujemy go wprost. */}
                  {q.mediaState !== "ready" && (
                    <Badge variant="warning" className="ml-2">
                      {q.mediaState}
                    </Badge>
                  )}
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  {isLeader && !isCurrent && (
                    <Button variant="ghost" size="sm" onClick={() => room.send.skipTo(q.uid)}>
                      odtwórz
                    </Button>
                  )}
                  {/*
                    Usuwanie BEZ pytania o potwierdzenie: pozycję dokłada się
                    jednym kliknięciem, więc pomyłka kosztuje jedno kliknięcie.
                    Okno potwierdzenia zostawiamy usuwaniu plików, którego cofnąć
                    nie sposób.
                  */}
                  {mayEdit && !isCurrent && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => room.send.queueRemove(q.uid)}
                      aria-label={`Usuń z kolejki: ${q.titleLabel}`}
                      className="hover:text-destructive"
                    >
                      <XIcon />
                    </Button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

function Chat({ room }: { room: ReturnType<typeof useRoom> }) {
  const [draft, setDraft] = useState("");
  /** Wspólna z wyszukiwaniem preferencja urządzenia — patrz `lib/device-pref.ts`. */
  const [keyboard, setKeyboard] = useState(() => readFlag(KEYBOARD_PREF, false));
  const listRef = useRef<HTMLUListElement>(null);

  // Przewijamy na dół po każdej wiadomości — czat, który tego nie robi, wymaga
  // ręcznego przewijania przy każdej odpowiedzi.
  useEffect(() => {
    // Pusta lista nie ma czego przewijać; przy okazji czyni zależność jawnym
    // użyciem, a nie samym wyzwalaczem.
    if (room.chat.length === 0) return;
    const list = listRef.current;
    if (list !== null) list.scrollTop = list.scrollHeight;
  }, [room.chat]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          Czat
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col">
        {/* Przewijanie własne, nie `ScrollArea`: listę przewija kod po każdej
            wiadomości, a to wymaga bezpośredniego dostępu do elementu z paskiem —
            Radix trzyma go we własnym, wewnętrznym węźle. */}
        <ul ref={listRef} className="mb-3 flex max-h-72 flex-col gap-1 overflow-y-auto text-sm">
          {room.chat.map((m) => (
            <li key={m.id} className="break-words">
              <span
                className={
                  m.type === "system" ? "text-muted-foreground italic" : "font-medium text-accent"
                }
              >
                {m.author}
              </span>
              <span className="text-muted-foreground">: {m.content}</span>
            </li>
          ))}
        </ul>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const text = draft.trim();
            if (text.length === 0) return;
            room.send.chat(text);
            setDraft("");
          }}
          className="flex gap-2"
        >
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Napisz…"
            maxLength={1000}
            aria-label="Wiadomość"
          />
          {/*
            Klawiatura ekranowa także tutaj, nie tylko w wyszukiwaniu.
            Czat jest JEDYNYM miejscem, gdzie się pisze podczas oglądania — a pilot
            wysyła strzałki i Enter, więc bez niej pole jest ozdobą. Przełącznik jest
            wspólny z wyszukiwaniem (`localStorage`), bo to preferencja urządzenia:
            kto ogląda na telewizorze, chce jej w obu miejscach.
          */}
          <Button
            type="button"
            variant={keyboard ? "secondary" : "ghost"}
            size="icon"
            aria-pressed={keyboard}
            aria-label="Klawiatura ekranowa"
            title="Klawiatura ekranowa"
            onClick={() => {
              const next = !keyboard;
              setKeyboard(next);
              writeFlag(KEYBOARD_PREF, next);
            }}
          >
            <KeyboardIcon />
          </Button>
          <Button type="submit" size="icon" aria-label="Wyślij">
            <SendIcon />
          </Button>
        </form>

        {keyboard && <OnScreenKeyboard value={draft} onChange={setDraft} />}
      </CardContent>
    </Card>
  );
}

/**
 * Wskaźnik zgodności jednego uczestnika.
 *
 * Cztery różne rzeczy, które muszą być rozróżnialne, bo prowadzą do różnych
 * decyzji: zgodny (nic nie robić), rozjechany (można poprosić o odświeżenie),
 * zatrzymany u siebie (czeka, nie zgubił się), milczący (zamknął kartę albo
 * uśpił telefon — i wtedy stara wartość byłaby kłamstwem podanym jako świeża).
 *
 * Próg zgodności to `DRIFT_THRESHOLD`, ten sam, przy którym follower sam się
 * poprawia — inaczej wskaźnik alarmowałby o czymś, co za sekundę zniknie samo.
 */
function SyncBadge({ state }: { state: MemberSyncView | null }) {
  if (state === null || state.driftSec === null || state.playing === null) {
    return (
      <span className="ml-auto text-xs text-muted-foreground" title="Brak świeżego zgłoszenia">
        —
      </span>
    );
  }

  if (!state.playing) {
    return (
      <Badge variant="outline" className="ml-auto">
        <PauseIcon />
        zatrzymane
      </Badge>
    );
  }

  const drift = state.driftSec;
  if (Math.abs(drift) <= DRIFT_THRESHOLD) {
    return (
      <Badge variant="success" className="ml-auto">
        <CheckIcon />
        zgodny
      </Badge>
    );
  }

  return (
    <Badge
      variant="warning"
      className="ml-auto tabular-nums"
      title={drift > 0 ? "Jest dalej niż pokój" : "Jest za pokojem"}
    >
      <TriangleAlertIcon />
      {drift > 0 ? "+" : ""}
      {drift.toFixed(1)} s
    </Badge>
  );
}
